import React, { useState, useEffect } from 'react';
import Canvas from '../canvas/Canvas';
import ImgCanvas from '../canvas/ImgCanvas';
import FloatingHUD from './FloatingHUD';
import SlideNavButtons from './SlideNavButtons';
import SlideReactionsOverlay from './SlideReactionsOverlay';

function useVisibleHeight() {
    const getHeight = () => (window.visualViewport?.height ?? window.innerHeight);
    const [height, setHeight] = useState(getHeight);
    useEffect(() => {
        const update = () => setHeight(getHeight());
        const vp = window.visualViewport;
        if (vp) {
            vp.addEventListener('resize', update);
            return () => vp.removeEventListener('resize', update);
        }
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return height;
}

function ContainedSlide({ cursor, img = document.createElement('img'), roommateIds = [], roomId, readOnly }) {
    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                justifyContent: 'center'
            }}>
            <ImgCanvas img={img} />
            <Canvas cursor={cursor} img={img} roommateIds={roommateIds} roomId={roomId} readOnly={readOnly} />
        </div>
    );
}

function FullScreenSlide({
    cursor,
    img = document.createElement('img'),
    roommateIds = [],
    roomId,
    hudActions = [],
    readOnly,
    isAdmin = false,
    onPrevSlide,
    onNextSlide,
    hasPrevSlide = false,
    hasNextSlide = false
}) {
    const viewportHeight = useVisibleHeight();
    return (
        <div
            className='hide-scrollbars'
            style={{
                position: 'fixed',
                height: viewportHeight + 'px',
                width: '100vw',
                top: 0,
                left: 0,
                display: 'flex',
                justifyContent: 'center',
                background: 'black',
                zIndex: 1198
            }}
            onTouchMove={(e) => e.preventDefault()}
            onMouseMove={(e) => e.preventDefault()}>
            <ImgCanvas img={img} />
            <Canvas cursor={cursor} img={img} roommateIds={roommateIds} roomId={roomId} readOnly={readOnly} />
            {hudActions.length ? <FloatingHUD actions={hudActions} /> : null}
            {isAdmin && (
                <SlideNavButtons
                    onPrevSlide={onPrevSlide}
                    onNextSlide={onNextSlide}
                    hasPrevSlide={hasPrevSlide}
                    hasNextSlide={hasNextSlide}
                />
            )}
        </div>
    );
}

const Vyur = ({
    fullscreen,
    img = document.createElement('img'),
    roommateIds = [],
    roomId,
    cursor,
    hudActions = [],
    isAdmin = false,
    onPrevSlide,
    onNextSlide,
    hasPrevSlide = false,
    hasNextSlide = false,
    readOnly = false,
    slideId,
    showReactions = false
}) => {
    const [newImage, setNewImage] = useState(null);

    useEffect(() => {
        const image = new Image();

        const handleLoad = () => {
            setNewImage(image);
        };
        image.addEventListener('load', handleLoad);

        if (typeof img.src !== 'string') {
            const blob = URL.createObjectURL(new Blob(img.src), {
                type: 'image/jpeg',
                quality: 1,
                width: img.width,
                height: img.height
            });
            image.src = blob;
        } else {
            image.src = img.src;
        }

        // For already-cached images where 'load' may not fire
        if (image.complete && image.naturalWidth > 0) {
            setNewImage(image);
        }

        return () => {
            image.removeEventListener('load', handleLoad);
        };
    }, [fullscreen, img, roommateIds, roomId, cursor]);

    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden'
            }}
        >
            {fullscreen ? (
                <FullScreenSlide
                    cursor={cursor}
                    img={newImage}
                    roommateIds={roommateIds}
                    roomId={roomId}
                    hudActions={hudActions}
                    readOnly={readOnly}
                    isAdmin={isAdmin}
                    onPrevSlide={onPrevSlide}
                    onNextSlide={onNextSlide}
                    hasPrevSlide={hasPrevSlide}
                    hasNextSlide={hasNextSlide}
                />
            ) : (
                <ContainedSlide
                    cursor={cursor}
                    img={newImage}
                    roommateIds={roommateIds}
                    roomId={roomId}
                    readOnly={readOnly}
                />
            )}
            {!fullscreen && hudActions.length ? <FloatingHUD actions={hudActions} /> : null}
            {isAdmin && !fullscreen && (
                <SlideNavButtons
                    onPrevSlide={onPrevSlide}
                    onNextSlide={onNextSlide}
                    hasPrevSlide={hasPrevSlide}
                    hasNextSlide={hasNextSlide}
                />
            )}
            {showReactions && slideId && (
                <SlideReactionsOverlay
                    roomId={roomId}
                    slideId={slideId}
                    fullscreen={fullscreen}
                />
            )}
        </div>
    );
};

export default Vyur;
